import {
  ArrowDownTrayIcon,
  ArrowsPointingOutIcon,
  MinusIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useState, useEffect, useCallback, type MouseEvent, type TouchEvent } from 'react';
import { useObjectUrl } from '../hooks/useObjectUrl';
import { useBlobText } from '../hooks/useBlobText';
import { downloadBlob } from '../../../utils/base64UrlConverters';
import { isXmlMimeType } from '../../../utils/mimeTypes';
import UblViewer from '../../../components/general/UblViewer';



type DocumentPreviewFallbackProps = {
  title: string;
  subtitle?: string;
  extraInfo?: string;
  onDownload?: () => void;
  downloadFileName: string;
  downloadLabel?: string;
};

const DocumentPreviewFallback = ({
  title,
  subtitle,
  extraInfo,
  onDownload,
  downloadFileName,
  downloadLabel = 'Bestand downloaden',
}: DocumentPreviewFallbackProps) => (
  <div className="flex h-full flex-col items-center justify-center p-6 text-center">
    <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-gray-500">
      <ArrowDownTrayIcon className="h-5 w-5" aria-hidden="true" />
    </span>
    <p className="mb-1 text-sm font-medium text-gray-800">{title}</p>
    {subtitle && <p className="text-xs leading-5 text-gray-500">{subtitle}</p>}
    {extraInfo && <p className="mt-1 text-xs text-gray-400">{extraInfo}</p>}
    {onDownload && (
      <button
        type="button"
        onClick={onDownload}
        className="mt-5 inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-sm transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary active:scale-[0.97]"
      >
        <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
        {downloadLabel}
      </button>
    )}
    <p className="mt-2 max-w-full truncate text-[11px] text-gray-400" title={downloadFileName}>{downloadFileName}</p>
  </div>
);

export type DocumentContent = {
  file_name: string;
  mimetype: string;
  file_blob?: Blob;
}

type DocumentPreviewProps = {
  document: DocumentContent | null
}

const getPdfDownloadFileName = (fileName?: string): string => {
  const pdfBaseName = (fileName || 'document').replace(/\.pdf$/i, '');
  return `${pdfBaseName}.pdf`;
};

export const DocumentPreview = ({ document }: DocumentPreviewProps) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const { url: documentObjectUrl, error: objectUrlError } = useObjectUrl(
    document?.file_blob,
  );
  const isXmlDocument = isXmlMimeType(document?.mimetype);
  const xmlString = useBlobText(document?.file_blob, isXmlDocument);

  const closeFullScreen = useCallback(() => {
    setIsFullScreen(false);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
    setIsDragging(false);
  }, []);

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeFullScreen();
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => {
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [closeFullScreen]);

  const handleZoomIn = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
  }, []);

  const handleZoomOut = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  }, []);

  const handleCloseFullScreen = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    closeFullScreen();
  }, [closeFullScreen]);

  const handleMouseDown = useCallback((e: MouseEvent<HTMLImageElement>) => {
    if (zoomLevel > 1) {
      e.stopPropagation();
      e.preventDefault();
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  }, [zoomLevel, position]);

  const handleTouchStart = useCallback((e: TouchEvent<HTMLImageElement>) => {
    if (zoomLevel > 1) {
      e.stopPropagation();
      e.preventDefault();
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({ x: touch.clientX - position.x, y: touch.clientY - position.y });
    }
  }, [zoomLevel, position]);

  const handleMouseMove = useCallback((e: globalThis.MouseEvent) => {
    if (isDragging && zoomLevel > 1) {
      e.stopPropagation();
      e.preventDefault();
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      setPosition({ x: newX, y: newY });
    }
  }, [isDragging, zoomLevel, dragStart]);

  const handleTouchMove = useCallback((e: globalThis.TouchEvent) => {
    if (isDragging && zoomLevel > 1) {
      e.stopPropagation();
      e.preventDefault();
      const touch = e.touches[0];
      const newX = touch.clientX - dragStart.x;
      const newY = touch.clientY - dragStart.y;
      setPosition({ x: newX, y: newY });
    }
  }, [isDragging, zoomLevel, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isFullScreen) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);

      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
      window.addEventListener('touchcancel', handleTouchEnd);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);

        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
        window.removeEventListener('touchcancel', handleTouchEnd);
      };
    }
  }, [isFullScreen, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);



  if (!document || !document.file_blob) {
    return (
      <DocumentPreviewFallback
        title="Geen documentvoorbeeld beschikbaar"
        subtitle="Het bronbestand ontbreekt of kon niet worden geladen."
        downloadFileName={document?.file_name || 'document'}
      />
    )
  }

  if (objectUrlError) {
    return (
      <DocumentPreviewFallback
        title="Bestand kon niet worden geladen."
        subtitle="Probeer opnieuw of download het bestand."
        onDownload={() => downloadBlob(document.file_blob!, document.file_name || 'document')}
        downloadFileName={document.file_name || 'document'}
      />
    );
  }

  if (document.mimetype?.includes('image')) {
    return (
      <>
        <div className="group relative flex h-full min-h-0 w-full items-center justify-center overflow-hidden bg-gray-100 p-3">
          <button
            type="button"
            onClick={() => setIsFullScreen(true)}
            className="flex h-full min-h-0 w-full items-center justify-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            aria-label="Document op volledig scherm bekijken"
          >
            <img
              src={documentObjectUrl || ''}
              alt="Documentvoorbeeld"
              className="max-h-full max-w-full object-contain shadow-sm"
              draggable="false"
            />
          </button>
          <div className="pointer-events-none absolute right-5 top-5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
            <span className="inline-flex items-center gap-1.5 rounded-md bg-gray-900/80 px-2.5 py-1.5 text-[11px] font-medium text-white shadow-lg backdrop-blur-sm">
              <ArrowsPointingOutIcon className="h-3.5 w-3.5" aria-hidden="true" />
              Vergroten
            </span>
          </div>
        </div>
        {isFullScreen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-gray-950/90 p-4 backdrop-blur-sm"
            onClick={closeFullScreen}
            role="dialog"
            aria-modal="true"
            aria-label="Document op volledig scherm"
          >
            <button
              type="button"
              className="absolute right-4 top-4 z-[60] rounded-full bg-gray-800 p-2 text-white shadow-lg transition-[background-color,transform] duration-150 hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-white active:scale-[0.97]"
              onClick={handleCloseFullScreen}
              aria-label="Volledig scherm sluiten"
            >
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>

            <div className="absolute left-4 top-4 z-[60]">
              <a
                href={documentObjectUrl || undefined}
                download={document.file_name || 'document'}
                className="inline-block rounded-full bg-gray-800 p-2 text-white shadow-lg transition-[background-color,transform] duration-150 hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-white active:scale-[0.97]"
                aria-label="Document downloaden"
              >
                <ArrowDownTrayIcon className="h-6 w-6" aria-hidden="true" />
              </a>
            </div>

            <div className="absolute bottom-4 left-1/2 z-[60] flex -translate-x-1/2 items-center gap-2 rounded-full bg-gray-800 p-1.5 shadow-lg">
              <button
                type="button"
                className="rounded-full p-2 text-white transition-[background-color,transform] duration-150 hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-white active:scale-[0.97]"
                onClick={handleZoomOut}
                disabled={zoomLevel <= 0.5}
                aria-label="Uitzoomen"
              >
                <MinusIcon className="h-5 w-5" aria-hidden="true" />
              </button>
              <span className="min-w-12 text-center text-xs font-semibold text-white">{Math.round(zoomLevel * 100)}%</span>
              <button
                type="button"
                className="rounded-full p-2 text-white transition-[background-color,transform] duration-150 hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-white active:scale-[0.97]"
                onClick={handleZoomIn}
                disabled={zoomLevel >= 3}
                aria-label="Inzoomen"
              >
                <PlusIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            <img
              src={documentObjectUrl || ''}
              alt="Documentvoorbeeld op volledig scherm"
              className="max-h-screen max-w-full object-contain"
              draggable="false"
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${zoomLevel})`,
                transition: zoomLevel === 1 ? 'transform 0.2s cubic-bezier(0.77, 0, 0.175, 1)' : 'none',
                cursor: isDragging && zoomLevel > 1 ? 'grabbing' : (zoomLevel > 1 ? 'grab' : 'default')
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
            />
          </div>
        )}
      </>
    )
  } else if (document.mimetype === 'application/pdf') {
    const pdfDownloadFileName = getPdfDownloadFileName(document.file_name);

    return (
      <div className="relative h-full min-h-0 w-full bg-gray-100 p-2">
        <object
          data={documentObjectUrl}
          type="application/pdf"
          className="h-full min-h-[18rem] w-full rounded-md bg-white shadow-sm"
        >
          <iframe
            src={documentObjectUrl}
            className="h-full min-h-[18rem] w-full border-0"
            title="PDF-documentvoorbeeld"
          >
            <div className="flex flex-col items-center justify-center h-full text-center">
              <p className="text-gray-500 mb-4">Je browser ondersteunt geen PDF preview.</p>
              <div className="space-y-2">
                <a
                  href={documentObjectUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-blue-600 hover:text-blue-800 underline"
                >
                  PDF openen in nieuw tabblad
                </a>
                <a
                  href={documentObjectUrl}
                  download={pdfDownloadFileName}
                  className="block text-green-600 hover:text-green-800 underline"
                >
                  PDF downloaden
                </a>
              </div>
            </div>
          </iframe>
        </object>
      </div>
    )
  } else if (isXmlDocument) {
    if (xmlString === null) {
      return (
        <DocumentPreviewFallback
          title="Bestand kon niet worden geladen."
          subtitle="Probeer opnieuw of download het bestand."
          onDownload={() => downloadBlob(document.file_blob!, document.file_name || 'document')}
          downloadFileName={document.file_name || 'document'}
        />
      );
    }
    return (
      <div className="h-full w-full overflow-auto bg-gray-100 p-3">
        <UblViewer xmlString={xmlString} />
      </div>
    )
  } else {
    return (
      <DocumentPreviewFallback
        title="Dit bestandstype kan niet worden weergegeven in de browser"
        subtitle={`Bestandstype: ${document.mimetype}`}
        extraInfo="Ondersteunde types: PDF, JPEG, PNG"
        onDownload={documentObjectUrl ? () => downloadBlob(document.file_blob!, document.file_name || 'document') : undefined}
        downloadFileName={document.file_name || 'document'}
      />
    )
  }
}
